import type { StudioEffect, StudioEffectType } from "@loumy/contracts";
import { create } from "zustand";
import { addEffect, removeEffect, reorderEffect, toggleEffect, updateEffect } from "./effectStack";
import type { StudioResultKind, StudioSegment } from "./studioPlan";
import { fullTimeline, outputFrameCount, reorder, setKeep, setRange, splitAt } from "./studioPlan";

// §9.17 — Studio-UI-State (zustand). Hält NUR den serialisierbaren Zustand: Quelle/Meta,
// Timeline-Segmente, Playhead, EFFEKT-STACK, Ergebnis-Art, Grid-Vermutung, Status. Die FRAMES
// (ImageData[]) liegen BEWUSST NICHT im Store (zu groß) — sie leben in einem Ref in StudioShell.
// Der BG-Remove ist seit dem Editor-Ausbau KEIN fester Schalter mehr, sondern ein `chroma-key`-
// Effekt im komponierbaren `effects`-Stack (frei umordnen/togglen/löschen).

export type StudioSourceKind = "image" | "video" | "spritesheet" | "animation";
export type StudioStatus = "empty" | "loading" | "ready" | "saving";

/** Grid-Vermutung-Subset (== contracts.GridGuess, nur die UI-relevanten Felder). */
export interface StudioGrid {
  cols: number;
  rows: number;
  frameCount: number;
  method: "alpha" | "chroma" | "none";
  detectedKey: number[] | null;
}

let segCounter = 0;
/** Kollisionsfreie Segment-id (kein Math.random — deterministisch je Session-Lauf). */
export function nextSegId(suffix = ""): string {
  segCounter += 1;
  return `seg-${segCounter}${suffix}`;
}

let effCounter = 0;
/** Kollisionsfreie Effekt-id (deterministisch je Session-Lauf). */
export function nextEffectId(): string {
  effCounter += 1;
  return `fx-${effCounter}`;
}

/** Auswahl auf einen GÜLTIGEN (behaltenen) Clip auflösen: aktuelle behalten, falls noch behalten,
 *  sonst den ersten behaltenen Clip (oder null). Verhindert eine Auswahl auf einen gelöschten/
 *  weggeschnittenen Clip (dessen id nach split nicht mehr existiert). */
function resolveSelection(segments: StudioSegment[], current: string | null): string | null {
  if (current && segments.some((s) => s.id === current && s.keep)) return current;
  return segments.find((s) => s.keep)?.id ?? null;
}

/** Effekt-Stack-Mutation NUR auf dem selektierten Clip anwenden (Effekte sind clip-lokal).
 *  REFERENZ-STABIL: ändert `fn` den Stack nicht (gleiche Referenz, z.B. reorderEffect-No-op),
 *  bleibt das segments-Array unverändert (gleiche Referenz) — so erkennt `commit` den No-op. */
function patchSelectedEffects(
  segments: StudioSegment[],
  selectedId: string | null,
  fn: (effects: StudioEffect[]) => StudioEffect[],
): StudioSegment[] {
  let changed = false;
  const next = segments.map((seg) => {
    if (seg.id !== selectedId) return seg;
    const effects = fn(seg.effects);
    if (effects === seg.effects) return seg;
    changed = true;
    return { ...seg, effects };
  });
  return changed ? next : segments;
}

// ── Undo/Redo ────────────────────────────────────────────────────────────────
// Die History snapshotet NUR den serialisierbaren DOKUMENT-State (Timeline-Segmente inkl. Effekt-
// Stacks + Output-Einstellungen + Auswahl). Die FRAMES (ImageData[]) ändern sich durch diese Edits
// NICHT (Schnitt/Effekte sind non-destruktiv, leben in Refs) ⇒ kein Frame-Restore nötig. Da alle
// Mutatoren (studioPlan/effectStack) IMMUTABEL arbeiten (neue Arrays/Objekte), reicht ein FLACHER
// Snapshot — die unveränderten Teilbäume werden strukturell geteilt (kein Deep-Clone).
// Playhead/Playing/Status sind transient (Transport/Lifecycle) und NICHT Teil der History.
interface DocSnapshot {
  segments: StudioSegment[];
  selectedSegmentId: string | null;
  seam: boolean;
  resultKind: StudioResultKind;
  loop: boolean;
}

const HISTORY_LIMIT = 100; // Deckel je Stack (alte Schritte fallen unten raus)
// PRIMÄRE Geste-Grenze ist `endGesture()` (von einem globalen pointerup in StudioShell ausgelöst): ein
// Pointer-Drag (down→moves→up) bildet GENAU EINEN Undo-Schritt — pointerup nullt `_mergeKey`, der
// nächste Drag startet garantiert neu (kein fälschliches Verschmelzen zweier Aktionen). Das Zeitfenster
// ist nur ein FALLBACK für Eingaben OHNE pointerup (Tastatur-Slider, Pfeiltasten): gleiche mergeKey-
// Mutationen innerhalb des Fensters verschmelzen, eine längere Pause beginnt einen neuen Schritt.
const MERGE_WINDOW_MS = 1000;

function captureDoc(s: StudioState): DocSnapshot {
  return { segments: s.segments, selectedSegmentId: s.selectedSegmentId, seam: s.seam, resultKind: s.resultKind, loop: s.loop };
}

interface StudioState {
  status: StudioStatus;
  error: string | null;

  // Quelle (für den Save: sourceAssetId ist Pflicht; bei Upload erst nach api.uploadAsset gesetzt)
  sourceAssetId: string | null;
  sourceKind: StudioSourceKind | null;
  totalFrames: number;
  frameWidth: number;
  frameHeight: number;
  fps: number;
  grid: StudioGrid | null;

  // Timeline
  segments: StudioSegment[];
  playhead: number; // Index in die OUTPUT-Sequenz
  playing: boolean;
  // Welcher Clip im Effekt-Panel bearbeitet wird (§9.17 — Effekte gelten PRO Clip). null = keiner.
  selectedSegmentId: string | null;

  // Output-Einstellungen (KEINE reorderbaren Effekte)
  seam: boolean; // RIFE-Naht (nur Sheet)
  resultKind: StudioResultKind;
  loop: boolean; // Sheet-Meta (loop=on) — speist spritesheet_pack + Naht-Sinn

  // Undo/Redo-History (Dokument-Snapshots; Komponente liest past/future.length für die Button-State)
  past: DocSnapshot[];
  future: DocSnapshot[];
  _mergeKey: string | null; // letzter Geste-Schlüssel (Coalescing zusammenhängender Drags)
  _mergeAt: number; // Zeitstempel des letzten Edits (ms) für das Merge-Fenster

  // Aktionen
  reset: () => void;
  undo: () => void;
  redo: () => void;
  /** Geste-Grenze: beendet das Coalescing-Fenster (globaler pointerup) — der nächste Edit beginnt
   *  garantiert einen neuen Undo-Schritt, auch wenn sein mergeKey dem letzten gleicht. */
  endGesture: () => void;
  setStatus: (status: StudioStatus, error?: string | null) => void;
  loaded: (meta: {
    sourceAssetId: string | null;
    sourceKind: StudioSourceKind;
    totalFrames: number;
    frameWidth: number;
    frameHeight: number;
    fps: number;
    grid: StudioGrid | null;
    resultKind: StudioResultKind;
    loop: boolean;
  }) => void;
  setSourceAssetId: (id: string) => void;
  setPlayhead: (i: number) => void;
  setPlaying: (p: boolean) => void;
  setSelectedSegment: (id: string | null) => void;
  cutAtPlayhead: (outputIndex: number) => void;
  toggleKeep: (id: string, keep: boolean) => void;
  trimSegment: (id: string, from: number, to: number) => void;
  moveSegment: (from: number, to: number) => void;
  // Effekt-Stack — alle Mutationen gelten dem SELEKTIERTEN Clip (Effekte sind clip-lokal).
  // Gibt die neue Effekt-id zurück — oder null, wenn kein Clip selektiert ist (kein Effekt erzeugt).
  addStudioEffect: (type: StudioEffectType) => string | null;
  removeStudioEffect: (id: string) => void;
  toggleStudioEffect: (id: string, enabled: boolean) => void;
  updateStudioEffect: (id: string, patch: Record<string, unknown>) => void;
  moveStudioEffect: (from: number, to: number) => void;
  // Output
  setSeam: (on: boolean) => void;
  setResultKind: (k: StudioResultKind) => void;
  setLoop: (on: boolean) => void;
  setGrid: (grid: StudioGrid) => void;
}

const INITIAL = {
  status: "empty" as StudioStatus,
  error: null as string | null,
  sourceAssetId: null as string | null,
  sourceKind: null as StudioSourceKind | null,
  totalFrames: 0,
  frameWidth: 0,
  frameHeight: 0,
  fps: 24,
  grid: null as StudioGrid | null,
  segments: [] as StudioSegment[],
  playhead: 0,
  playing: false,
  selectedSegmentId: null as string | null,
  seam: false,
  resultKind: "spritesheet" as StudioResultKind,
  loop: true,
  past: [] as DocSnapshot[],
  future: [] as DocSnapshot[],
  _mergeKey: null as string | null,
  _mergeAt: 0,
};

export const useStudioStore = create<StudioState>((set, get) => {
  // Eine UNDOBARE Mutation: vor dem Anwenden den aktuellen Doc-Snapshot in `past` schieben und den
  // Redo-Stack (`future`) leeren. Gehört die Mutation zu einer laufenden Geste (gleicher `mergeKey`
  // im Zeitfenster — Slider-/Trim-Drag), wird KEIN neuer Snapshot gepusht (der „vor der Geste"-Stand
  // bleibt als EIN Undo-Schritt erhalten). Alle übrigen (transienten) Setter bleiben plain `set`.
  const commit = (mutate: (s: StudioState) => Partial<StudioState>, mergeKey?: string) => {
    const s = get();
    const next = mutate(s);
    // No-op (mutate liefert {}/null): GAR KEIN set (kein leerer Undo-Schritt, kein Redo-Verwurf, und
    // KEINE unnötige Root-Subscriber-Benachrichtigung — Codex-R3). Die Mutatoren signalisieren den
    // No-op billig: Cut an Clipgrenze (gleiche Segment-Anzahl), Reorder gleicher Index, referenz-
    // gleicher Effekt-Stack (effectPatch/patchSelectedEffects).
    if (!next || Object.keys(next).length === 0) return;
    const now = Date.now();
    const merge = mergeKey != null && s._mergeKey === mergeKey && now - s._mergeAt < MERGE_WINDOW_MS;
    set({
      ...next,
      past: merge ? s.past : [...s.past, captureDoc(s)].slice(-HISTORY_LIMIT),
      future: [],
      _mergeKey: mergeKey ?? null,
      _mergeAt: now,
    });
  };

  // Effekt-Stack-Mutation des SELEKTIERTEN Clips → commit-Partial. Lässt `fn` den Stack
  // referenz-gleich (No-op: id nicht im Stack / Wert schon gesetzt / Reorder gleicher Index), bleibt
  // `segments` referenz-gleich ⇒ `{}` (kein leerer Undo-Schritt). Zentral für ALLE Effekt-Mutatoren.
  const effectPatch = (s: StudioState, fn: (fx: StudioEffect[]) => StudioEffect[]): Partial<StudioState> => {
    const segments = patchSelectedEffects(s.segments, s.selectedSegmentId, fn);
    return segments === s.segments ? {} : { segments };
  };

  return {
  ...INITIAL,

  reset: () => set({ ...INITIAL }),

  undo: () => {
    const s = get();
    const prev = s.past[s.past.length - 1];
    if (!prev) return; // leerer Stack ⇒ kein set (Button ohnehin disabled)
    // Aktuellen Stand für Redo sichern, vorherigen wiederherstellen. mergeKey zurücksetzen, damit
    // eine direkt folgende Geste NICHT in den wiederhergestellten Schritt hineinmergt.
    set({
      ...prev,
      past: s.past.slice(0, -1),
      future: [...s.future, captureDoc(s)].slice(-HISTORY_LIMIT),
      _mergeKey: null,
      _mergeAt: 0,
    });
  },
  redo: () => {
    const s = get();
    const nextDoc = s.future[s.future.length - 1];
    if (!nextDoc) return;
    set({
      ...nextDoc,
      future: s.future.slice(0, -1),
      past: [...s.past, captureDoc(s)].slice(-HISTORY_LIMIT),
      _mergeKey: null,
      _mergeAt: 0,
    });
  },
  endGesture: () => {
    if (get()._mergeKey !== null) set({ _mergeKey: null });
  },

  setStatus: (status, error = null) => set({ status, error }),

  loaded: (meta) => {
    const segments = fullTimeline(meta.totalFrames, (i) => nextSegId(`-${i}`));
    set({
      status: "ready",
      error: null,
      sourceAssetId: meta.sourceAssetId,
      sourceKind: meta.sourceKind,
      totalFrames: meta.totalFrames,
      frameWidth: meta.frameWidth,
      frameHeight: meta.frameHeight,
      fps: meta.fps,
      grid: meta.grid,
      resultKind: meta.resultKind,
      loop: meta.loop,
      segments,
      // Frische Quelle: EIN Clip ohne Effekte, direkt selektiert (das Panel bearbeitet ihn). Den
      // detectedKey-Vorschlag (Stufe B) verwandelt StudioShell ggf. in einen chroma-key-Effekt.
      selectedSegmentId: segments[0]?.id ?? null,
      playhead: 0,
      playing: false,
      seam: false,
      // Frische Quelle = neuer Ausgangszustand: History leeren (kein Undo über das Laden hinweg).
      past: [],
      future: [],
      _mergeKey: null,
      _mergeAt: 0,
    });
  },

  setSourceAssetId: (id) => set({ sourceAssetId: id }),
  setPlayhead: (i) => set({ playhead: Math.max(0, Math.trunc(i)) }),
  setPlaying: (playing) => set({ playing }),
  setSelectedSegment: (id) => set({ selectedSegmentId: id }),

  // outputIndex = Position in der OUTPUT-Sequenz; in einen ABSOLUTEN Frame-Index übersetzen
  // macht die Komponente (sie kennt resolveOutputIndices) — hier kommt der absolute Index rein.
  cutAtPlayhead: (absoluteFrameIndex) =>
    commit((s) => {
      const segments = splitAt(s.segments, absoluteFrameIndex, (sfx) => nextSegId(`-${sfx}`));
      // No-op: Playhead fällt auf eine Clipgrenze (kein neues Stück) ⇒ keine History (häufig via K-Taste).
      if (segments.length === s.segments.length) return {};
      // Nach dem Schnitt bevorzugt die RECHTE Hälfte selektieren (from===Schnittpunkt) — der alte
      // Eltern-Clip (selektierte id) existiert nicht mehr. Sonst Auswahl auf gültigen Clip auflösen.
      const p = Math.trunc(absoluteFrameIndex);
      const half = segments.find((seg) => seg.keep && seg.from === p);
      return { segments, selectedSegmentId: half?.id ?? resolveSelection(segments, s.selectedSegmentId) };
    }),

  toggleKeep: (id, keep) =>
    commit((s) => {
      const segments = setKeep(s.segments, id, keep);
      let selectedSegmentId = s.selectedSegmentId;
      const selValid = !!selectedSegmentId && segments.some((seg) => seg.id === selectedSegmentId && seg.keep);
      if (!selValid) {
        // Auswahl ungültig (selektierter Clip gelöscht ODER vorher keiner): beim Wiederherstellen den
        // gerade wiederhergestellten Clip wählen (Codex-P2), sonst den ersten behaltenen.
        selectedSegmentId = keep ? id : resolveSelection(segments, null);
      }
      return { segments, selectedSegmentId };
    }),
  // Trimmen feuert pro pointermove → der ganze Drag verschmilzt über `trim:<id>` zu EINEM Undo-Schritt.
  trimSegment: (id, from, to) =>
    commit((s) => ({ segments: setRange(s.segments, id, from, to, s.totalFrames) }), `trim:${id}`),
  moveSegment: (from, to) =>
    commit((s) => {
      const segments = reorder(s.segments, from, to);
      return segments === s.segments ? {} : { segments }; // gleicher Index ⇒ No-op (reorder ist referenz-stabil)
    }),

  addStudioEffect: (type) => {
    // Ohne selektierten Clip greift kein Effekt: KEINE id erzeugen, KEINE History (UI sperrt das
    // ohnehin) — sonst gäbe der Aufrufer eine id zurück, die im Store nicht existiert (Codex-R2).
    if (!get().selectedSegmentId) return null;
    const id = nextEffectId();
    commit((s) => effectPatch(s, (fx) => addEffect(fx, type, id)));
    return id;
  },
  removeStudioEffect: (id) => commit((s) => effectPatch(s, (fx) => removeEffect(fx, id))),
  toggleStudioEffect: (id, enabled) => commit((s) => effectPatch(s, (fx) => toggleEffect(fx, id, enabled))),
  // Slider feuern pro Wert → ein Drag desselben FELDES verschmilzt über `update:<id>:<felder>` zu
  // EINEM Schritt (verschiedene Felder/Effekte = getrennte Schritte).
  updateStudioEffect: (id, patch) =>
    commit((s) => effectPatch(s, (fx) => updateEffect(fx, id, patch)), `update:${id}:${Object.keys(patch).sort().join(",")}`),
  moveStudioEffect: (from, to) => commit((s) => effectPatch(s, (fx) => reorderEffect(fx, from, to))),

  setSeam: (on) => commit(() => ({ seam: on })),
  setResultKind: (k) =>
    // Einzelframe-PNG hat keine Naht (Naht ist Loop-Sheet-Sache).
    commit((s) => ({ resultKind: k, seam: k === "image" ? false : s.seam })),
  setLoop: (on) => commit((s) => ({ loop: on, seam: on ? s.seam : false })),
  setGrid: (grid) => set({ grid }),
  };
});

/** Abgeleitet: Output-Frame-Anzahl (für Playhead-Klemmung/Anzeige). */
export function studioOutputCount(s: Pick<StudioState, "segments" | "totalFrames">): number {
  return outputFrameCount(s.segments, s.totalFrames);
}
